import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, CardActions, CardContent, CardHeader, Chip, TextField, Typography } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { isEqual } from 'lodash'
import { useDispatch } from 'react-redux'

import { api } from '../../../services/api'
import { showError, showSuccess } from '../../../actions/misc/misc.actions'
import { getStaffById } from '../../../actions/Team/team.actions'
import { USER_ROLES } from '../../../utils'
import LoadingOverlayContained from '../../Misc/LoadingOverlayContained'

const roleSuggestions = Object.values(USER_ROLES)

const getErrorMessage = (e, fallback) => e?.response?.data?.message || e?.data?.message || fallback

const StaffFirebaseClaimsCard = ({ staffId }) => {
	const dispatch = useDispatch()
	const [uid, setUid] = useState(null)
	const [claims, setClaims] = useState(null)
	const [roles, setRoles] = useState([])
	const [loading, setLoading] = useState(false)
	const [loadError, setLoadError] = useState(null)

	const applyResponse = useCallback((data) => {
		setUid(data?.uid || null)
		setClaims(data?.claims || {})
		setRoles(data?.claims?.roles || [])
	}, [])

	const loadClaims = useCallback(async () => {
		setLoading(true)
		setLoadError(null)
		try {
			const { data } = await api.get(`/team/${staffId}/firebase-claims`)
			applyResponse(data)
		} catch (e) {
			setLoadError(getErrorMessage(e, 'Could not load Firebase claims.'))
		} finally {
			setLoading(false)
		}
	}, [staffId, applyResponse])

	useEffect(() => {
		if (staffId) loadClaims()
	}, [staffId, loadClaims])

	const saveRoles = async () => {
		setLoading(true)
		try {
			const { data } = await api.put(`/team/${staffId}/firebase-claims`, { roles })
			applyResponse(data)
			dispatch(showSuccess('Firebase roles saved.'))
			// Refresh the staff form so its next save sends these roles instead of the old ones.
			dispatch(getStaffById(staffId))
		} catch (e) {
			dispatch(showError(getErrorMessage(e, 'Could not save Firebase roles.')))
		} finally {
			setLoading(false)
		}
	}

	const savedRoles = claims?.roles || []
	const hasChanges = !isEqual(roles, savedRoles)

	return (
		<Card style={{ marginTop: 24, position: 'relative' }}>
			<CardHeader title="Firebase Custom Claims" subheader={uid ? `Admin uid: ${uid}` : null} />
			<CardContent>
				{loadError ? (
					<Typography color="error">{loadError}</Typography>
				) : (
					<>
						<Autocomplete
							multiple
							freeSolo
							options={roleSuggestions.filter((role) => !roles.includes(role))}
							value={roles}
							onChange={(e, value) => {
								const next = value.map((role) => String(role).trim()).filter(Boolean)
								setRoles([...new Set(next)])
							}}
							renderTags={(value, getTagProps) =>
								value.map((role, index) => <Chip key={role} label={role} {...getTagProps({ index })} />)
							}
							renderInput={(inputProps) => (
								<TextField
									{...inputProps}
									variant="outlined"
									label="roles"
									helperText="Type any value and press Enter to add it, including roles outside the usual list."
								/>
							)}
						/>
						<Typography variant="subtitle2" style={{ marginTop: 24 }}>
							Raw claims
						</Typography>
						<pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
							{JSON.stringify(claims || {}, null, 2)}
						</pre>
						<Typography variant="caption" color="textSecondary">
							Changes take effect the next time this staff member's session refreshes its token, usually within an hour,
							or immediately after they sign out and back in.
						</Typography>
					</>
				)}
			</CardContent>
			<CardActions>
				<Button variant="contained" color="secondary" disabled={loading || !hasChanges || !!loadError} onClick={saveRoles}>
					Save Firebase Roles
				</Button>
				<Button variant="outlined" color="secondary" disabled={loading || !hasChanges} onClick={() => setRoles(savedRoles)}>
					Reset
				</Button>
				<Button disabled={loading} onClick={loadClaims}>
					Reload
				</Button>
			</CardActions>
			{loading ? <LoadingOverlayContained /> : null}
		</Card>
	)
}

export default StaffFirebaseClaimsCard
